import React, { useMemo } from "react";
import { Box, Button } from "@mui/material";
import { BaseWidgetProps, WidgetWrapper } from "shared-ui";
import { useOrgUnitIdCurrent } from "../../../../auth/useOrgUnitIdCurrent";
import { useClaims } from "../../../../auth/ClaimsProvider";
import { ShiOnePermission } from "../../../../auth/authorizationTypes";
import useAddToCartModal from "@features/ecommerce/cart/useAddToCartModal";
import AddToCartModal from "@features/ecommerce/cart/AddToCartModal";
import useGetFeaturedProduct from "@features/ecommerce/hooks/useGetFeaturedProduct";
import useStorefrontCurrency from "@features/ecommerce/hooks/useStorefrontCurrency";
import ProductList, { ProductListItem } from "./Components/ProductList";
import CatalogedProduct from "shared-ui/src/types/ecommerce/CatalogedProduct";
import { BUNDLE_PRODUCT_CLASS } from "shared-ui/src/types/ecommerce/constants";
import { getShiComBaseUrl, navigateToShiCom } from "@lib/shiComBaseUrl";
import { useFeatureFlag } from "shared-ui/src/utils/FeatureFlagHelper";
import { useNavigate } from "react-router-dom";
import { AppendIfPrefixExists } from "../../../../modules/sitePaths";

const baseUrl = getShiComBaseUrl();
const contactAccountTeamUrl = `${baseUrl}/CustomerService/contact-us`;

export default function StandardsFeaturedProducts({
  pageLayoutWidget,
}: Readonly<BaseWidgetProps>) {
  const navigate = useNavigate();
  const orgUnitId = useOrgUnitIdCurrent();
  const { hasPermission } = useClaims();
  const { enabled: standardsShopEnabled } = useFeatureFlag(
    "nomads.enable-standards-shop",
    false,
  );
  const featuredProductsView = hasPermission(
    ShiOnePermission.featuredProductsView,
  );
  const canAddToCart = hasPermission(ShiOnePermission.shoppingCartSave);
  const { formatAmount } = useStorefrontCurrency();

  const { data, isLoading, isError } = useGetFeaturedProduct(
    featuredProductsView,
    orgUnitId,
    true, //In Catalog
    12, //Top
  );
  const { addToCart, modalProps } = useAddToCartModal();

  const products = useMemo((): ProductListItem[] => {
    if (!data?.length) {
      return [];
    }
    return data
      .filter((p) => p.productDescriptor && p.productClass)
      // Config-system (bundle) products go through the Configure flow and must never reach the
      // single-add modal path, which attaches a warranty and would insert a duplicate device line.
      .filter(
        (p) =>
          p.productClass?.toLowerCase() !== BUNDLE_PRODUCT_CLASS,
      )
      .map(
        (p): CatalogedProduct => ({
          id: `${p.productDescriptor!}|${p.productClass}`,
          productDescriptor: p.productDescriptor,
          productClass: p.productClass,
          name: p.productName,
          description: p.parsedDescriptionsList,
          manufacturerPartNumber: p.mfrPartNum,
          shiPartNumber: p.productDescriptor,
          price: p.price,
          imageUrl: p.imageUrl,
          availability: p.inventoryStatus,
          canAddToCart: p.canAddToCart,
        }),
      );
  }, [data]);

  if (!featuredProductsView) {
    return null;
  }

  return (
    <WidgetWrapper
      pageLayoutWidget={{
        ...pageLayoutWidget,
      }}
      error={isError}
      isEmpty={!isLoading && products.length === 0}
      cardHeaderDivider
      noPadding
      cardActions={
        <Button
          aria-label="View all standards products"
          onClick={(e) => {
            e.stopPropagation();
            if (standardsShopEnabled) {
              navigate(AppendIfPrefixExists("/standards-shop"));
              return;
            }
            navigateToShiCom("/shop/standards");
          }}
        >
          VIEW ALL STANDARDS
        </Button>
      }
      errorContactAccountTeamLink={contactAccountTeamUrl}
      hideWidgetContentOnError={true}
    >
      <Box
        component="section"
        aria-label="Featured standards products"
        sx={{ pt: 1 }}
      >
        <ProductList
          products={products}
          formatPrice={formatAmount}
          onAddToCart={canAddToCart ? addToCart : undefined}
        />
      </Box>
      {modalProps.open && <AddToCartModal {...modalProps} />}
    </WidgetWrapper>
  );
}
